/* eslint-disable max-lines-per-function */
/* eslint-disable no-irregular-whitespace */
import { Avatar, Button, classNames, Spacing, Subhead, Title } from '@vkontakte/vkui';
import vkLogo from 'img/vkmusic-bundle/vk-logo.png';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { type VK_MUSIC_BUNDLE_SOURCE, VK_MUSIC_RADAR_NAMES } from 'reactApp/modules/vkMusic/vkMusic.const';
import type { VKIDInfo } from 'reactApp/modules/vkMusic/vkMusic.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './VkMusicConfirmModal.css';
import { getAvatarSrc } from './VkMusicConfirmModal.helpers';

export interface VkMusicConfirmModalProps {
    mailUser?: {
        email: string;
        fullName: string;
    };
    vkidUser: VKIDInfo;
    canChoose?: boolean;
    onChooseClick?: () => void;
    onClose?: () => void;
    onClick?: () => void;
    onShow?: () => void;
    onCancel?: () => void;
    source: VK_MUSIC_BUNDLE_SOURCE;
}

export const VkMusicConfirmModal = memo<VkMusicConfirmModalProps>(
    ({ mailUser, vkidUser, onClose, onClick, onShow, onCancel, onChooseClick, canChoose = true, source }) => {
        const isVKIDSocialUser = useSelector(UserSelectors.isVKIDSocialUser);

        const sendAnalytics = useCallback((action: string, restArgs = {}) => {
            sendPaymentGa({
                action,
                name: `${VK_MUSIC_RADAR_NAMES.CONFIRM_MODAL}${isVKIDSocialUser ? '-social' : ''}`,
                eventCategory: ECategoryGa.modal,
                offer_source: source,
                ...restArgs,
            });
        }, []);

        useEffect(() => {
            sendAnalytics('show');
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_CONFIRM_SHOW, { source });
            onShow?.();
        }, []);

        const handleOnClick = useCallback(() => {
            sendAnalytics('click', { button_name: 'continue' });
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_CONFIRM_CONTINUE, { source });
            onClick?.();
        }, []);

        const handleOnChooseClick = useCallback(() => {
            sendAnalytics('click', { button_name: 'choose' });
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_CONFIRM_CHOOSE, { source });
            onChooseClick?.();
        }, []);

        const handleOnCancel = useCallback(() => {
            sendAnalytics('close');
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_CONFIRM_CLOSE, { source });
            onCancel?.();
        }, []);

        const content = useMemo(() => {
            return (
                <>
                    <Spacing size={8} />
                    <div data-qa-id="text">
                        Посмотрите на данные аккаунта: имя, {vkidUser.phone ? 'номер телефона' : 'фамилию'} и фотографию. Если всё верно,
                        подключим подписку.
                        {canChoose && ' Если хотите слушать музыку с другого аккаунта — выберите другой'}
                    </div>
                    {!canChoose && <Spacing size={8} />}
                    {canChoose && (
                        <>
                            <Button
                                size="l"
                                mode="link"
                                appearance="accent-invariable"
                                onClick={handleOnChooseClick}
                                data-qa-id="choose-vkid-button"
                            >
                                Выбрать другой аккаунт
                            </Button>
                        </>
                    )}
                    <Spacing size={canChoose ? 16 : 24} />
                </>
            );
        }, []);

        const profileBanner = useMemo(() => {
            return (
                <div data-qa-id="profile-banner" className={styles.profileBanner}>
                    <div className={styles.avatars}>
                        {mailUser && <Avatar withBorder={false} size={72} src={getAvatarSrc(mailUser.email, mailUser.fullName, 144)} />}
                        <Avatar
                            withBorder={false}
                            className={classNames(styles.vkidAvatar, {
                                [styles.single]: !mailUser,
                            })}
                            size={72}
                            src={vkidUser.avatar || getAvatarSrc(`${vkidUser.login}@vk`, vkidUser.firstName, 144)}
                        >
                            <img className={styles.vkLogo} src={vkLogo} />
                        </Avatar>
                    </div>
                    <Spacing size={12} />
                    <Title data-qa-id="name" level="3">
                        {vkidUser.firstName} {vkidUser.lastName}
                    </Title>
                    {vkidUser.phone && (
                        <Subhead data-qa-id="phone" className={styles.phone}>
                            {vkidUser.phone}
                        </Subhead>
                    )}
                </div>
            );
        }, []);

        return (
            <WhatsNewDialog
                className={styles.root}
                imageUrl={''}
                imageOnlyAdditionalContent
                imageWrapperClassName={classNames(styles.imageWrapper, styles.small)}
                imageClassName={styles.image}
                contentClassName={classNames(styles.content, styles.small)}
                title="Проверьте VK ID — к нему подключим VK Музыку"
                content={content}
                imageAdditionalContent={profileBanner}
                primaryButtonText={canChoose ? 'Продолжить с этим VK ID' : 'Продолжить'}
                secondaryButtonText="Не сейчас"
                onSecondary={handleOnCancel}
                closeBySecondaryButton
                closeAfterClick
                onClick={handleOnClick}
                onClose={onClose}
                onDismiss={handleOnCancel}
                buttonTheme="vk"
                dialogSize="tiny"
                qaId="vkmusic_confirm_modal"
                isRebranding
            />
        );
    }
);

VkMusicConfirmModal.displayName = 'VkMusicConfirmModal';
